import React, { useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { TFunction } from "i18next/typescript/t";

import FilterRenderer from "@/components_v2/Filters/FilterRenderer";
import { FilterField } from "@/components_v2/Filters/types";
import {
  HookFormBaseDropdown,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import { OrganizationRoles } from "@/shared/utils/constants";
import {
  MemberFiltersProps,
  MemberFiltersState,
} from "@/features/organizations/profile/types";

import { useFilterCount } from "@/shared/hooks/useFilterCount";

import "../styles.css";

const memberFiltersSchema = (
  t: TFunction,
): yup.ObjectSchema<MemberFiltersState> =>
  yup
    .object()
    .shape({
      excludeUsers: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid"))),
      search: yup
        .string()
        .transform((value) => (value === "" ? undefined : value))
        .typeError(t("form.validation.notValid")),
      roles: yup
        .array()
        .of(yup.string().required().typeError(t("form.validation.notValid"))),
    })
    .required();

const MemberFilters = ({
  filterNames,
  setFilters,
  defaultFilters,
}: MemberFiltersProps) => {
  const { t } = useTranslation();

  const form = useForm<MemberFiltersState>({
    resolver: yupResolver(memberFiltersSchema(t)),
    mode: "onChange",
    defaultValues: {
      search: "",
      roles: [],
      ...defaultFilters,
    },
  });

  const filterFields: Record<string, FilterField> = useMemo(() => {
    return {
      search: {
        name: "search",
        isAdvancedFilter: false,
        isDebounced: true,
        component: (
          <HookFormInput
            name="search"
            control={form.control}
            className="org-profile-filters-search"
            suffix={<SearchOutlined />}
            placeholder={t("organizations.placeholders.searchMembers")}
          />
        ),
      },
      roles: {
        name: "roles",
        label: t("organizations.labels.roles"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="roles"
            control={form.control}
            multiple
            options={OrganizationRoles.asList()}
            placeholder={t("organizations.placeholders.selectRoles")}
            allowClear={true}
          />
        ),
      },
    };
  }, []);

  const activeFilterCount = useFilterCount<MemberFiltersState>(
    form,
    defaultFilters,
  );

  return (
    <FilterRenderer
      form={form}
      filters={filterFields}
      activeFilterNames={filterNames}
      popoverTitle={t("organizations.labels.filters")}
      handleClear={() => {
        form.reset(defaultFilters);
        setFilters(form.getValues());
      }}
      onFiltersChange={setFilters}
      areFiltersDirty={form.formState.isDirty}
      activeFilterCount={activeFilterCount}
      handleSearch={() => setFilters(form.getValues())}
      className="filter-layout"
    />
  );
};

export default MemberFilters;
